/**
 * 本地工具 read_attachment（docs/语音助手.md 第 1 部分「本地工具」、4.4，与 iOS AttachmentReader 行为一致）：
 * 读取家长在对话里发过的附件。不填 asset_id 时列出最近发过的附件；填了就下载原件，
 * 按 attachments.ts 同样的方式提取文字（PDF 按页、带页码标记）。
 * 依赖（附件列表、下载、打开 PDF）由 agent.ts 传进来（webReadDeps），方便测试。
 */
import { attachmentKind, decodeText, pageRange, pagesText, READ_CHARS } from "./attachments";

/** 对话里记下的一个附件（消息 attachments 里的信息 + 发送时间） */
export interface SentAttachment {
  asset_id: string;
  name: string;
  kind?: string;
  mime?: string;
  size?: number;
  page_count?: number;
  /** 发送时间（毫秒） */
  sent_at: number;
}

export interface PdfSource {
  numPages: number;
  pageText(n: number): Promise<string>;
  close(): void;
}

export interface ReadDeps {
  /** 家长最近发过的附件（最新的在前） */
  listSent(): Promise<SentAttachment[]>;
  /** 某个附件的信息：对话里记下的，或同步下来的附件表；都没有时 null */
  info(assetId: string): Promise<{ name: string; mime: string; size?: number } | null>;
  /** 下载原件 */
  download(assetId: string): Promise<ArrayBuffer>;
  /** 打开 PDF；打不开返回 null */
  openPdf(data: ArrayBuffer): Promise<PdfSource | null>;
  /** 时间戳 → 家庭时区的"YYYY-MM-DD HH:mm" */
  formatTime(ms: number): string;
}

export const READ_ATTACHMENT_TOOL = {
  name: "read_attachment",
  title: "读附件",
  description:
    "读取家长在对话里发过的附件（PDF、文本、图片；附件已经上传，消息里的「附件 id」就是 asset_id）。不填 asset_id 时列出家长最近发过的附件（名称、asset_id、类型、大小、发送时间）。填 asset_id 时返回内容：PDF 按页返回文字（带「— 第 n 页 —」标记、总页数，一次最多约 9000 字，没读完的用 pages 接着读），文本文件用 offset 接着读；图片在工具结果里看不到。需要附件原文、消息里没附全的页、或较早发过的附件时用；要把 PDF 布置成阅读内容时，create_contents 的 payload.pdf_asset_id 直接填这个 asset_id。",
  parameters: {
    type: "object",
    properties: {
      asset_id: { type: "string", description: "附件 id；不填时列出最近发过的附件" },
      pages: { type: "string", description: 'PDF 的页码范围，如 "3-5"、"7"、"6-"；不填从第 1 页读起' },
      offset: { type: "integer", description: "文本文件从第几个字开始读（接着上次没读完的地方），默认 0" },
    },
  },
  readOnly: true,
  destructive: false,
};

export const PDF_USAGE = "创建 PDF 阅读内容：create_contents 的 activity_type=pdf_reader，payload.pdf_asset_id 用这个 asset_id（只读其中几页时加 page_range，如 [3, 5]）";
export const IMAGE_USAGE = "这张图片可以直接用作学习内容的 cover_asset_id，或 grant_cards 的 photo_asset_ids";
const LIST_NOTE = "用 asset_id 调用 read_attachment 读取内容；PDF 可以直接作为 create_contents（activity_type=pdf_reader）的 payload.pdf_asset_id。更早的附件可以用 search_history 搜文件名（消息里写着附件 id）。";
const EMPTY_NOTE = "最近的对话里家长没有发过附件。更早的附件可以用 search_history 搜文件名（消息里写着附件 id）。";
const IMAGE_NOTE = "这是图片。家长发送时已经给你看过（只在那一轮能看到），工具结果里不能再看图片；需要再看请家长重新发送。";
const SCANNED_NOTE = "这个 PDF 是扫描件，提取不到文字。家长发送时前 5 页已经作为图片给你看过（只在那一轮能看到），工具结果里不能看图片；需要内容请家长重新发送或描述。";

type Result = { text: string; isError: boolean };
const ok = (obj: unknown): Result => ({ text: JSON.stringify(obj), isError: false });
const fail = (error: string): Result => ({ text: JSON.stringify({ error }), isError: true });

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export async function readAttachment(args: Record<string, unknown>, deps: ReadDeps): Promise<Result> {
  const id = typeof args.asset_id === "string" ? args.asset_id.trim() : "";
  if (!id) return list(deps);
  return read(id, args, deps);
}

async function list(deps: ReadDeps): Promise<Result> {
  const sent = await deps.listSent();
  const attachments = sent.map((a) => ({
    asset_id: a.asset_id,
    name: a.name,
    mime: a.mime ?? "",
    kind: attachmentKind(a.mime ?? "", a.name),
    ...(a.size != null ? { size: a.size } : {}),
    ...(a.page_count != null ? { page_count: a.page_count } : {}),
    ...(a.sent_at > 0 ? { sent_at: deps.formatTime(a.sent_at) } : {}),
  }));
  return ok({ attachments, note: attachments.length ? LIST_NOTE : EMPTY_NOTE });
}

async function read(id: string, args: Record<string, unknown>, deps: ReadDeps): Promise<Result> {
  const info = await deps.info(id);
  if (!info) return fail(`找不到附件 ${id}。不填 asset_id 调用 read_attachment 可以列出家长发过的附件`);
  const kind = attachmentKind(info.mime, info.name);
  const obj: Record<string, unknown> = { asset_id: id, name: info.name, mime: info.mime, ...(info.size != null ? { size: info.size } : {}), kind };
  if (kind === "image") return ok({ ...obj, note: IMAGE_NOTE, usage: IMAGE_USAGE });
  if (kind !== "pdf" && kind !== "text") return ok({ ...obj, note: "这种文件读不了内容（只支持 PDF、文本和图片）。" });
  let data: ArrayBuffer;
  try {
    data = await deps.download(id);
  } catch (e) {
    return fail(`下载附件失败：${errorText(e)}`);
  }
  if (kind === "pdf") {
    const doc = await deps.openPdf(data);
    if (!doc || doc.numPages <= 0) {
      doc?.close();
      return fail(`「${info.name}」打不开，可能不是有效的 PDF`);
    }
    try {
      const count = doc.numPages;
      obj.page_count = count;
      obj.usage = PDF_USAGE;
      const range = pageRange(typeof args.pages === "string" ? args.pages : null, count);
      if (!range) return fail(`pages 格式不对或超出页数（这个 PDF 共 ${count} 页），例如 "3-5"、"7"`);
      const [from, to] = range;
      const pages = await pagesText(from, to, READ_CHARS, (n) => doc.pageText(n));
      if (pages.chars === 0) return ok({ ...obj, note: SCANNED_NOTE });
      obj.pages = pages.lastPage > from ? `${from}-${pages.lastPage}` : `${from}`;
      obj.text = pages.text;
      if (pages.cutPage != null) obj.cut = `第 ${pages.cutPage} 页太长，只返回了前面一部分`;
      if (pages.lastPage < to) {
        const next = pages.lastPage + 1;
        const rest = next === to ? `${next}` : `${next}-${to}`;
        obj.more = `还有第 ${rest} 页没读，用 pages="${rest}" 接着读`;
      }
      return ok(obj);
    } finally {
      doc.close();
    }
  }
  const all = [...decodeText(data)];
  const start = Math.min(Math.max(Math.floor(Number(args.offset) || 0), 0), all.length);
  const end = Math.min(start + READ_CHARS, all.length);
  obj.total_chars = all.length;
  obj.offset = start;
  obj.text = all.slice(start, end).join("");
  if (end < all.length) obj.more = `还有 ${all.length - end} 字没读，用 offset=${end} 接着读`;
  return ok(obj);
}
